import { motion, AnimatePresence } from "motion/react";
import { Icon } from "../../components/Icon";
import { useArtists, useTaxonomy, useVocabulary } from "../../api/queries";
import { useT } from "../../i18n/strings";
import { termLabel } from "../../i18n/labels";
import { micro } from "../../motion";
import { haptic } from "../../telegram/haptics";
import type { Filters } from "./filterState";
import s from "./filters.module.css";

interface ActiveChip {
  key: string;
  label: string;
  clear: Partial<Filters>;
}

/**
 * What is filtering the list, spelled out.
 *
 * A bare "3 filters active" makes the reader open the sheet to find out which
 * three; a chip per filter answers it in place and lets one be dropped without
 * a round trip through the sheet.
 */
export function ActiveFilters({
  filters,
  onChange,
  onClearAll,
}: {
  filters: Filters;
  onChange: (changes: Partial<Filters>) => void;
  onClearAll: () => void;
}) {
  const t = useT();
  const taxonomy = useTaxonomy();
  const mediums = useVocabulary("medium");
  const styles = useVocabulary("style");
  const ownership = useVocabulary("ownership_status");
  const condition = useVocabulary("condition_status");
  const artists = useArtists("");

  const type = taxonomy.data?.find((row) => row.id === filters.type);
  const subtype = type?.subtypes.find((row) => row.id === filters.subtype);
  const chips: ActiveChip[] = [];

  const push = (key: string, label: string, clear: Partial<Filters>) => {
    if (label) chips.push({ key, label, clear });
  };

  push("type", termLabel(type), { type: null, subtype: null });
  push("subtype", termLabel(subtype), { subtype: null });
  push("artist", artists.data?.find((row) => row.id === filters.artist)?.name ?? "", {
    artist: null,
  });
  push("style", termLabel(styles.data?.find((row) => row.id === filters.style)), {
    style: null,
  });
  push(
    "ownership_status",
    termLabel(ownership.data?.find((row) => row.id === filters.ownership_status)),
    { ownership_status: null },
  );
  push(
    "condition_status",
    termLabel(condition.data?.find((row) => row.id === filters.condition_status)),
    { condition_status: null },
  );

  for (const id of filters.medium) {
    push("medium-" + id, termLabel(mediums.data?.find((row) => row.id === id)), {
      medium: filters.medium.filter((other) => other !== id),
    });
  }

  const { year_from: from, year_to: to } = filters;
  if (from !== null || to !== null) {
    const label =
      from !== null && to !== null
        ? t.filters.yearRange(String(from), String(to))
        : from !== null
          ? t.filters.yearSince(String(from))
          : t.filters.yearUntil(String(to));
    push("year", label, { year_from: null, year_to: null });
  }
  if (filters.year_unknown) push("year_unknown", t.filters.unknownYear, { year_unknown: false });
  if (filters.has_primary_media !== null) {
    push("has_primary_media", t.filters.hasMedia, { has_primary_media: null });
  }

  if (!chips.length) return null;

  return (
    <div className={s.activeRow}>
      <div className={s.activeChips}>
        <AnimatePresence initial={false} mode="popLayout">
          {chips.map((chip) => (
            <motion.button
              key={chip.key}
              type="button"
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={micro}
              className={s.activeChip}
              aria-label={t.filters.removeOne(chip.label)}
              onClick={() => {
                haptic.select();
                onChange(chip.clear);
              }}
            >
              <span className={s.activeChipLabel}>{chip.label}</span>
              <Icon name="close" size={16} />
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      {chips.length > 1 && (
        <button
          type="button"
          className={s.clearAllButton}
          onClick={() => {
            haptic.tap();
            onClearAll();
          }}
        >
          {t.actions.clearAll}
        </button>
      )}
    </div>
  );
}
